import { LoadingConstants, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import React from 'react';
import { UserManagementStateContext } from '../providers/UserManagementStateProvider';
import type { UserManagementUserProfileDto, Zuid } from '@zg-rentals/ts-user-management-client';
import {
  UmcCompanyOverviewWebApiHandler_getUserCompanyActivePropertyCount,
  UmcCompanyOverviewWebApiHandler_getUserManagementActiveProfiles,
} from '@zg-rentals/ts-user-management-client';
import type { UserPropertyData, UserState } from './userStates';

export const useActiveUsersInternal = (): UserState => {
  const { user } = useRentalManagerUser();
  const [loadingState, setLoadingState] = React.useState<LoadingConstants>(LoadingConstants.INITIAL);
  const [profiles, setProfiles] = React.useState<Array<UserManagementUserProfileDto>>([]);
  const [propertyData, setPropertyData] = React.useState<Record<string, UserPropertyData>>({});
  const [propertyDataLoadingState, setPropertyDataLoadingState] = React.useState<LoadingConstants>(
    LoadingConstants.INITIAL,
  );

  const getActiveUsers = async () => {
    if (!user.userManagementCompanyId) {
      return;
    }
    let hasMoreData = true;
    let zuid: Zuid | undefined = undefined;
    const profiles: Array<UserManagementUserProfileDto> = [];
    try {
      while (hasMoreData) {
        const response = await UmcCompanyOverviewWebApiHandler_getUserManagementActiveProfiles({ zuid });
        hasMoreData = response.hasMoreData;
        if (response.profiles.length > 0) {
          zuid = response.profiles[response.profiles.length - 1].userToken;
          profiles.push(...response.profiles);
        }
      }
    } catch (error) {
      setLoadingState(LoadingConstants.ERROR);
      setProfiles([]);
      return;
    }
    setProfiles(profiles);
    setLoadingState(LoadingConstants.SUCCESS);

    setPropertyDataLoadingState(LoadingConstants.LOADING);
    try {
      await Promise.all(
        profiles.map(async (profile) => {
          const userId = profile.userToken || profile.email;
          const result = await UmcCompanyOverviewWebApiHandler_getUserCompanyActivePropertyCount({
            zuid: profile.userToken,
          });
          setPropertyData((prev) => ({
            ...prev,
            [userId]: {
              propertyCount: result.activePropertyCount,
              userId,
              loadingState: LoadingConstants.SUCCESS,
              olympusTier3ListingCount: result.freeBonusListingCount,
            },
          }));
        }),
      );
      setPropertyDataLoadingState(LoadingConstants.SUCCESS);
    } catch (error) {
      setPropertyDataLoadingState(LoadingConstants.ERROR);
      return;
    }
  };

  React.useEffect(() => {
    getActiveUsers();
  }, [user.userManagementCompanyId]);

  const state = React.useMemo(() => {
    return {
      loadingState,
      profiles,
      propertyData,
      propertyDataLoadingState,
    };
  }, [loadingState, profiles, propertyData, propertyDataLoadingState]);
  return state;
};

export const useActiveUsers = (): UserState => {
  const umcState = React.useContext(UserManagementStateContext);

  if (!umcState) throw new Error('State Not initialized');

  return React.useMemo(() => umcState.activeUsersState, [umcState.activeUsersState]);
};
